import React from "react"
import { ActionDialog, AlertMessage, Button, Link, TextField, Typography, theme } from "cdh-data-portal-theme"
import type { LineManagerAssignment } from "../AssignLineManagerDeputy/AssignLineManagerDeputy.static"
import { useRemoveLineManagerAssignments } from "./RemoveLineManagerAssignments.logic"
import { useStyles } from "./RemoveLineManagerAssignments.style"
import { localtext, type RemoveLineManagerAssignmentsProps } from "./RemoveLineManagerAssignments.static"

interface AssignmentCardProps {
  readonly assignment: LineManagerAssignment
  readonly onRemove: () => void
}

const AssignmentCard: React.FC<AssignmentCardProps> = ({ assignment, onRemove }) => {
  const { classes } = useStyles()

  return (
    <div className={classes.card}>
      <AssignmentDetail label={localtext.CurrentLineManager} value={assignment.currentLineManager} />
      <AssignmentDetail label={localtext.TemporaryDeputy} value={assignment.temporaryDeputy} />
      <AssignmentDetail label={localtext.Submitted} value={new Date(assignment.submittedAt).toLocaleString()} />
      <AssignmentDetail label={localtext.StartingDate} value={assignment.startingDate} />
      <AssignmentDetail label={localtext.EndDate} value={assignment.endDate} />
      <AssignmentDetail label={localtext.Ticket} value={assignment.ticketUrl} isLink />
      <Button className={classes.cardAction} title={localtext.RemoveAction} variant="alert" onClick={onRemove} />
    </div>
  )
}

interface AssignmentDetailProps {
  readonly isLink?: boolean
  readonly label: string
  readonly value: string
}

const AssignmentDetail: React.FC<AssignmentDetailProps> = ({ isLink, label, value }) => {
  const { classes } = useStyles()

  return (
    <div className={classes.detail}>
      <Typography variant="label2" color={theme.textColors.primary}>{label}</Typography>
      {isLink ? <Link url={value}>{value}</Link> : <Typography variant="body">{value}</Typography>}
    </div>
  )
}

export const RemoveLineManagerAssignments: React.FC<RemoveLineManagerAssignmentsProps> = ({ assignments, onRemove }) => {
  const { classes } = useStyles()
  const {
    closeRemovalDialog,
    filteredAssignments,
    openRemovalDialog,
    removalTicketUrl,
    selectedAssignment,
    setRemovalTicketUrl,
    setSearchTerm,
    searchTerm,
  } = useRemoveLineManagerAssignments(assignments)
  const removalTicketError = !removalTicketUrl
    ? localtext.TicketRequired
    : removalTicketUrl !== selectedAssignment?.ticketUrl
      ? localtext.RemoveDialogTicketMismatch
      : undefined

  const confirmRemoval = () => {
    if (!selectedAssignment || removalTicketError) return

    // TODO: wire backend line_manager.remove mutation with the removal ticket before updating the list.
    onRemove(selectedAssignment.id)
    closeRemovalDialog()
  }

  return (
    <>
      {!assignments.length ? (
        <AlertMessage title={localtext.EmptyTitle} message={localtext.EmptyMessage} variant="info" />
      ) : (
        <>
          <TextField
            className={classes.search}
            label={localtext.SearchLabel}
            placeholder={localtext.SearchPlaceholder}
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
          />
          <div className={classes.cardList}>
            {filteredAssignments.length ? (
              filteredAssignments.map(assignment => (
                <AssignmentCard key={assignment.id} assignment={assignment} onRemove={() => openRemovalDialog(assignment.id)} />
              ))
            ) : (
              <AlertMessage title={localtext.SearchEmptyTitle} message={localtext.SearchEmptyMessage} variant="info" />
            )}
          </div>
        </>
      )}
      <ActionDialog
        acceptButtonVariant="alert"
        actionText={localtext.RemoveDialogAction}
        disableConfirm={Boolean(removalTicketError)}
        isOpen={Boolean(selectedAssignment)}
        title={localtext.RemoveDialogTitle}
        warning={{ warningMessage: localtext.RemoveDialogWarning, warningTitle: localtext.RemoveDialogWarningTitle }}
        onAccept={confirmRemoval}
        onCancel={closeRemovalDialog}
      >
        <Typography className={classes.dialogDescription} variant="body">
          {selectedAssignment && localtext.RemoveDialogDescription(selectedAssignment.currentLineManager, selectedAssignment.temporaryDeputy)}
        </Typography>
        <TextField
          label={localtext.RemoveDialogTicket}
          placeholder="https://jira.example.com/browse/CDHX-12345"
          value={removalTicketUrl}
          errorText={removalTicketError}
          onChange={event => setRemovalTicketUrl(event.target.value)}
        />
      </ActionDialog>
    </>
  )
}
